import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { BsChevronUp } from 'react-icons/bs';
import { useSelector, useDispatch } from 'react-redux';

import BayModal from '../../BayModal';
import BayToast from '../../BayToast';
import {
	colorsArr,
	launchpadCustomizationDefaultState,
} from '../../utilities/constants/launchpadConstants';
import ClientLogo from '../../BayLogo/ClientBAY.svg';
import { PRODUCT_LOGO_ALT } from '../../BayLogo/constants';
//import FinancialLogo from '../../BayLogo/FinancialBAY.svg';
import SitesLogo from '../../BayLogo/SitesBAY.svg';
import SocialLogo from '../../BayLogo/SocialBAY.svg';

import LaunchpadGridLayout from './LaunchpadGridLayout';
import SettingsDrawer from './SettingsDrawer';

// Module scope: an in-body component is a new type every render, so the node remounts.
const customToggle = React.forwardRef<HTMLDivElement, { children?: React.ReactNode; onClick?: (e: React.MouseEvent) => void }>(({ children, onClick }, ref) => (
	<div
		ref={ref}
		className="d-flex align-items-center w-100 cursor-pointer"
		onClick={(e) => {
			e.preventDefault();
			onClick?.(e);
		}}
	>
		{children}
	</div>
));

interface ColorCodes {
	iconColor?: string;
	iconBgColor?: string;
	linkColor?: string;
}

interface SelectedTheme {
	colorName?: string;
	iconHexColor?: string;
	bgHexColor?: string;
	bgColor?: string;
	isDefault?: boolean;
}

interface NavigationSettings {
	expandLeftNav?: boolean;
	showLaunchpadOnLogin?: boolean;
}

interface SnackbarState {
	show?: boolean;
	description?: string;
	variant?: string;
	position?: string;
	dismissible?: boolean;
}

interface User {
	platform?: string;
	parent_account_id?: string;
	ActingAsBAY_PARENT_ACCOUNT_ID?: string;
	PUBLICID?: string;
	_companyid?: string;
	ActingAsPUBLICID?: string;
	BAY_PARENT_ACCOUNT_ID?: string;
}

interface UserProductAccess {
	client?: boolean;
	social?: boolean;
	financial?: boolean;
	sites?: boolean;
	socialBayAccessProhibited?: boolean;
}

interface LaunchpadContentProps {
	t: (key: string) => string;
	user?: User;
	bannerType?: string;
	widgets?: any[];
	dispatchCurrentData?: (data: any) => void;
	getWidgetByIdFn?: (id: string) => any;
	existingLayout?: any;
	snackbar?: SnackbarState;
	setSnackbar?: (snackbar: SnackbarState) => void;
	handleClose?: () => void;
	currentBreakpoint?: string;
	setCurrentBreakpoint?: (breakpoint: string) => void;
	onWidthChange?: (width: number) => void;
	setIsInPreviewMode?: (preview: boolean) => void;
	widgetCustomizationData?: any;
	setWidgetCustomizationData?: (data: any) => void;
	headerImageUrl?: string;
	setWidgetFinishStatus?: (status: any) => void;
	widgetFinishStatus?: any;
	setHeaderImageUrl?: (url: string) => void;
	editModalInputFields?: any;
	setEditModalInputFields?: (fields: any) => void;
	marketingWidgetFields?: any;
	bannerImageUrl?: string;
	setBannerImageUrl?: (url: string) => void;
	otherLinksWidgetFields?: any;
	bannerWidgetFields?: any;
	financialWidgetFields?: any;
	listingsWidgetFields?: any;
	websitesWidgetFields?: any;
	crmWidgetFields?: any;
	adsAndLeadsWidgetFields?: any;
	landingPagesWidgetFields?: any;
	listingsSmcWidgetFields?: any;
	updateLaunchpadApi?: any;
	selectLaunchpadSettingsData?: any;
	selectUserProductAccess?: any;
	selectLaunchpadStatus?: any;
	uploadHeaderImageApi?: any;
	uploadBannerImageApi?: any;
	launchpadSettingsData?: any;
}

const LaunchpadContent: React.FC<LaunchpadContentProps> = (props) => {
	const {
		t,
		user,
		bannerType,
		widgets = [],
		dispatchCurrentData = () => {},
		getWidgetByIdFn = () => {},
		existingLayout,
		snackbar,
		setSnackbar = () => {},
		handleClose,
		currentBreakpoint,
		setCurrentBreakpoint = () => {},
		onWidthChange,
		setIsInPreviewMode = () => {},
		widgetCustomizationData,
		setWidgetCustomizationData = () => {},
		headerImageUrl,
		setWidgetFinishStatus = () => {},
		widgetFinishStatus,
		setHeaderImageUrl = () => {},
		editModalInputFields,
		setEditModalInputFields = () => {},
		marketingWidgetFields = {},

		bannerImageUrl,
		setBannerImageUrl = () => {},

		otherLinksWidgetFields = {},
		bannerWidgetFields = {},
		financialWidgetFields = {},
		listingsWidgetFields = {},
		websitesWidgetFields = {},
		crmWidgetFields = {},
		adsAndLeadsWidgetFields = {},
		landingPagesWidgetFields = {},
		listingsSmcWidgetFields = {},

		updateLaunchpadApi,
		selectLaunchpadSettingsData,
		selectUserProductAccess,
		selectLaunchpadStatus,
		//setLaunchpadSettingsData,
		uploadHeaderImageApi,
		uploadBannerImageApi,
		launchpadSettingsData,
	} = props;

	const launchpadSettingsDataState = useSelector(selectLaunchpadSettingsData);
	const launchpadStatus = useSelector(selectLaunchpadStatus);
	const userProductAccess: UserProductAccess = useSelector(selectUserProductAccess);

	const dispatch = useDispatch();

	const [editMode, setEditMode] = useState<boolean>(false);
	const [showConformationModal, setShowConformationModal] = useState<boolean>(false);
	const [isSettingsDrawerOpen, setIsSettingsDrawerOpen] = useState<boolean>(false);
	const [isRestoreDefault, setIsRestoreDefault] = useState<boolean>(false);
	const [showNoFeatureModal, setShowNoFeatureModal] = useState<boolean>(false);
	const [scroll, setScroll] = useState<boolean>(false);
	const [iseditable, setIseditable] = useState<boolean>(true);
	const [showWarningToast, setShowWarningToast] = useState<boolean>(false);
	const [isProductsVisible, setIsProductsVisible] = useState<boolean>(true);
	const [activeProduct, setActiveProduct] = useState<string>('');
	const [selectedTheme, setSelectedTheme] = useState<SelectedTheme>({});
	const [customThemeEnabled, setCustomThemeEnabled] = useState<boolean>(false);
	const [colorCodes, setColorCodes] = useState<ColorCodes>({});
	const [navigationSettings, setNavigationSettings] = useState<NavigationSettings>({});
	// eslint-disable-next-line no-unused-vars
	const [showLaunchpadOnLogin, setShowLaunchpadOnLogin] = useState<boolean>(false);

	const snackbarRef = useRef<number>(0);
	const prevWidgetsRef = useRef(widgets);

	useEffect(() => {
		return () => {
			setShowLaunchpadOnLogin(false);
		};
	}, [launchpadSettingsDataState]);

	useEffect(() => {
		if (user?.platform) {
			setActiveProduct(user?.platform);
		}
	}, [user]);

	useEffect(() => {
		if (launchpadStatus === 'loading') {
			setEditMode(false);
		}
	}, [launchpadStatus]);

	useEffect(() => {
		if (
			widgetFinishStatus &&
			Object.values(widgetFinishStatus)?.some((value) => value === true)
		) {
			setShowWarningToast(true);
		} else {
			setShowWarningToast(false);
		}
	}, [widgetFinishStatus]);

	useEffect(() => {
		const drawerTheme = launchpadSettingsData?.drawerThemeSettings;
		if (
			!isSettingsDrawerOpen &&
			isEmpty(widgetCustomizationData?.drawerThemeSettings)
		) {
			if (drawerTheme && !isEmpty(drawerTheme)) {
				setSelectedTheme(drawerTheme.theme);
				setColorCodes({
					iconColor: drawerTheme.iconColor,
					iconBgColor: drawerTheme.iconBgColor,
					linkColor: drawerTheme.linkColor,
				});
				setCustomThemeEnabled(drawerTheme.customThemeEnabled);
			} else {
				setSelectedTheme(colorsArr['blue']);
				setColorCodes({
					iconColor: colorsArr['blue'].iconHexColor,
					iconBgColor: colorsArr['blue'].bgHexColor,
					linkColor: colorsArr['blue'].iconHexColor,
				});
				setCustomThemeEnabled(false);
			}

			if (
				!isEmpty(launchpadSettingsData?.launchpadSettings?.navigationSettings)
			) {
				setNavigationSettings(
					launchpadSettingsData.launchpadSettings.navigationSettings,
				);
			} else {
				setNavigationSettings({
					expandLeftNav: false,
					showLaunchpadOnLogin: true,
				});
			}
		}
	}, [launchpadSettingsData, isSettingsDrawerOpen, widgetCustomizationData]);

	useEffect(() => {
		if (
			isSettingsDrawerOpen &&
			!isEmpty(widgetCustomizationData?.drawerThemeSettings)
		) {
			setSelectedTheme(widgetCustomizationData.drawerThemeSettings.theme);
			setColorCodes({
				iconColor: widgetCustomizationData.drawerThemeSettings.iconColor,
				iconBgColor: widgetCustomizationData.drawerThemeSettings.iconBgColor,
				linkColor: widgetCustomizationData.drawerThemeSettings.linkColor,
			});
			setCustomThemeEnabled(
				widgetCustomizationData.drawerThemeSettings.customThemeEnabled,
			);
		}
	}, [isSettingsDrawerOpen, widgetCustomizationData]);

	useEffect(() => {
		if (
			launchpadSettingsData?.launchpadSettings?.navigationSettings
				?.showLaunchpadOnLogin
		) {
			setShowLaunchpadOnLogin(true);
		}
	}, [launchpadSettingsData]);

	const setWidgets = (updatedWidgets: any) => {
		dispatchCurrentData(updatedWidgets);
	};

	const handleLaunchpadChanges = async (updatedLaunchpadData: any) => {
		let kvstoreId: string | undefined;

		// If social parent account id is present, use it as the kvstoreId
		if (user?.parent_account_id && user?.parent_account_id !== '') {
			kvstoreId = user?.parent_account_id;
		} else if (
			user?.ActingAsBAY_PARENT_ACCOUNT_ID &&
			user?.ActingAsBAY_PARENT_ACCOUNT_ID !== ''
		) {
			// If bay account id is present, use it as the kvstoreId
			kvstoreId = user?.ActingAsBAY_PARENT_ACCOUNT_ID;
		} else {
			// If neither parent account id nor bay account id is present,
			// use either the user's PUBLICID or the companyid if on the client platform
			kvstoreId =
				user?.platform === 'social' ? user?.PUBLICID : user?._companyid;
		}

		if (!isEmpty(updatedLaunchpadData)) {
			await dispatch(
				updateLaunchpadApi({
					component: 'launchpadSettings',
					realm: 'BAY',
					userId: kvstoreId,
					isCompanyContext: true,
					data: updatedLaunchpadData,
				}),
			)
				.unwrap()
				.then(() => {
					setSnackbar({
						show: true,
						description: t('customizationSuccessToast'),
					});
					setWidgetCustomizationData({});
					setIsInPreviewMode(false);
					setEditMode(!editMode);
					setWidgetFinishStatus({});
				})
				.catch((error: any) => {
					console.log('error', error);
					setSnackbar({
						show: true,
						description: t('somethingWentWrong'),
						variant: 'danger',
					});
				});
		}
	};

	const handleSaveLaunchpadConfig = async () => {
		const imagesToUpload: Promise<{ success: boolean; error?: any }>[] = [];
		let updatedWidgetData = { ...widgetCustomizationData };

		if (headerImageUrl) {
			const headerImagePromise = dispatch(uploadHeaderImageApi(headerImageUrl))
				.unwrap()
				.then((res: any) => {
					const uploadedImageUrl = res?.image_data
						? JSON.parse(res?.image_data)[0]?.url
						: '';
					updatedWidgetData = {
						...updatedWidgetData,
						widgets: {
							...updatedWidgetData.widgets,
							'widget-header': {
								...updatedWidgetData.widgets?.['widget-header'],
								headerImage: uploadedImageUrl,
							},
						},
					};
					return { success: true };
				})
				.catch((error: any) => {
					console.log('Header Image error inside', error);
					return { success: false, error };
				});

			imagesToUpload.push(headerImagePromise);
		}

		if (bannerImageUrl) {
			const bannerImagePromise = dispatch(uploadBannerImageApi(bannerImageUrl))
				.unwrap()
				.then((res: any) => {
					const uploadedImageUrl = res?.image_data
						? JSON.parse(res?.image_data)[0]?.url
						: '';
					updatedWidgetData = {
						...updatedWidgetData,
						widgets: {
							...updatedWidgetData.widgets,
							'widget-banner': {
								...updatedWidgetData.widgets?.['widget-banner'],
								bannerImage: uploadedImageUrl,
							},
						},
					};

					return { success: true };
				})
				.catch((error: any) => {
					return { success: false, error };
				});

			imagesToUpload.push(bannerImagePromise);
		}

		if (imagesToUpload.length > 0) {
			await Promise.all(imagesToUpload)
				.then(() => {
					const currentSettings = launchpadSettingsData || {};
					const savePayload = {
						...currentSettings,
						widgets: {
							...(currentSettings.widgets || {}),
							...updatedWidgetData.widgets,
						},
						launchpadSettings: {
							...(currentSettings.launchpadSettings || {}),
							...updatedWidgetData.launchpadSettings,
						},
						drawerThemeSettings: {
							...(currentSettings.drawerThemeSettings || {}),
							...updatedWidgetData.drawerThemeSettings,
						},
					};

					console.log(
						'handleSaveLaunchpadConfig - Final savePayload:',
						savePayload,
					);
					console.log(
						'handleSaveLaunchpadConfig - Final customThemeEnabled value:',
						savePayload.drawerThemeSettings?.customThemeEnabled,
					);

					handleLaunchpadChanges(savePayload);
				})
				.catch((error: any) => {
					console.log('Error in image uploads:', error);
					setSnackbar({
						show: true,
						description: t('errorUploadingImages'),
						variant: 'danger',
					});
				})
				.finally(() => {
					setHeaderImageUrl('');
					setBannerImageUrl('');
				});
		} else {
			const currentSettings = launchpadSettingsData || {};
			console.log(
				'handleSaveLaunchpadConfig - currentSettings:',
				currentSettings,
			);
			console.log(
				'handleSaveLaunchpadConfig - widgetCustomizationData:',
				widgetCustomizationData,
			);
			console.log(
				'handleSaveLaunchpadConfig - customThemeEnabled in drawerThemeSettings:',
				widgetCustomizationData?.drawerThemeSettings?.customThemeEnabled,
			);

			const savePayload = {
				...currentSettings,
				widgets: {
					...(currentSettings.widgets || {}),
					...widgetCustomizationData.widgets,
				},
				launchpadSettings: {
					...(currentSettings.launchpadSettings || {}),
					...widgetCustomizationData.launchpadSettings,
				},
				drawerThemeSettings: {
					...(currentSettings.drawerThemeSettings || {}),
					...widgetCustomizationData.drawerThemeSettings,
				},
			};

			console.log(
				'handleSaveLaunchpadConfig - Final savePayload:',
				savePayload,
			);
			console.log(
				'handleSaveLaunchpadConfig - Final customThemeEnabled value:',
				savePayload.drawerThemeSettings?.customThemeEnabled,
			);

			handleLaunchpadChanges(savePayload);
		}
	};

	const handleEditFunction = async () => {
		setEditMode(!editMode);
		if (editMode) {
			setSnackbar({
				show: true,
				description: t('customizationSuccessToast'),
			});
		}
	};

	const handleSubmit = async () => {
		if (isRestoreDefault) {
			const updatedWidgets = {
				launchpadSettings: {
					navigationSettings: {
						expandLeftNav: false,
						showLaunchpadOnLogin: true, // Default to true - users opt out
					},
					customThemeEnabled: false,
				},
				widgets: launchpadCustomizationDefaultState,
				drawerThemeSettings: {
					theme: colorsArr['blue'],
					iconColor: colorsArr['blue'].iconHexColor,
					iconBgColor: colorsArr['blue'].bgHexColor,
					linkColor: colorsArr['blue'].iconHexColor,
					customThemeEnabled: false,
				},
			};

			const updatedWidgetFinishStatus = {
				'widget-header': true,
				'widget-header-action': true,
				'widget-marketing': true,
				'widget-banner': true,
				'widget-crm': true,
				'widget-listings': true,
				'widget-websites': true,
				'widget-financial': true,
				'widget-otherLinks': true,
				'widget-adsAndLeads': true,
				'widget-listingsSmc': true,
				'widget-landingPages': true,
			};

			setSelectedTheme(colorsArr['blue']);
			widgetFinishStatus;
			setColorCodes({
				iconColor: colorsArr['blue'].iconHexColor,
				iconBgColor: colorsArr['blue'].bgHexColor,
				linkColor: colorsArr['blue'].iconHexColor,
			});
			setCustomThemeEnabled(false);

			setWidgetFinishStatus(updatedWidgetFinishStatus);
			setWidgetCustomizationData({ ...updatedWidgets });
			setShowConformationModal(false);
			setIsInPreviewMode(true);
			setSnackbar({
				show: true,
				description: t('launchPadRestoreDefaultSuccessToast'),
			});
			setEditModalInputFields({ ...updatedWidgets.widgets });
		} else {
			setEditMode(false);
			setShowConformationModal(false);
			setSnackbar({
				show: true,
				description: t('launchPadCustomizationChangesCanceledSuccessfully'),
			});
			setIsInPreviewMode(false);
			setWidgetCustomizationData({});
			setWidgetFinishStatus({});
			setEditModalInputFields((prev: any) => ({
				...prev,
				'widget-marketing': {
					...marketingWidgetFields,
				},
				'widget-otherLinks': {
					...otherLinksWidgetFields,
				},
				'widget-banner': {
					...bannerWidgetFields,
				},
				'widget-financial': {
					...financialWidgetFields,
				},
				'widget-listings': {
					...listingsWidgetFields,
				},
				'widget-websites': {
					...websitesWidgetFields,
				},
				'widget-crm': {
					...crmWidgetFields,
				},
				'widget-adsAndLeads': {
					...adsAndLeadsWidgetFields,
				},
				'widget-listingsSmc': {
					...listingsSmcWidgetFields,
				},
				'widget-landingPages': {
					...landingPagesWidgetFields,
				},
			}));
			if (launchpadSettingsData?.drawerThemeSettings) {
				setSelectedTheme(launchpadSettingsData?.drawerThemeSettings?.theme);
				setColorCodes({
					iconColor: launchpadSettingsData?.drawerThemeSettings?.iconColor,
					iconBgColor: launchpadSettingsData?.drawerThemeSettings?.iconBgColor,
					linkColor: launchpadSettingsData?.drawerThemeSettings?.linkColor,
				});
				setCustomThemeEnabled(
					launchpadSettingsData.drawerThemeSettings.customThemeEnabled,
				);
			} else {
				setSelectedTheme(colorsArr['blue']);
				setColorCodes({
					iconColor: colorsArr['blue'].iconHexColor,
					iconBgColor: colorsArr['blue'].bgHexColor,
					linkColor: colorsArr['blue'].iconHexColor,
				});
				setCustomThemeEnabled(false);
			}
		}
	};

	const OnBreakpointChange = (newBreakpoint: string) => {
		setCurrentBreakpoint(newBreakpoint);

		if (['xs', 'xxs', 'xxxs'].includes(newBreakpoint)) {
			setEditMode(false);
			setIseditable(false);
		} else {
			setIseditable(true);
		}
	};

	const toggleProductsVisibility = () => {
		setIsProductsVisible(!isProductsVisible);
	};

	// MARK: - Product Toggle
	//
	const handleProductClick = (itemValue: string) => {
		setActiveProduct(itemValue);

		const smcredirect = document.getElementById('smcredirect') as HTMLFormElement | null;

		if (itemValue !== user?.platform) {
			switch (itemValue) {
			case 'social':
				if (smcredirect) {
					/* eslint-disable-next-line no-undef */
					(jQuery as any).iqUIProcessing('show');
					smcredirect.submit();
				}
				break;
			case 'client':
				if (user?.platform === 'social') {
					/* eslint-disable-next-line no-undef */
					(jQuery as any).useSSOForm();
				}
				break;
			case 'sites':
				window.location.href = `http://sites.${
					window.location.host.split('.')[1]
						? window.location.host.split('.')[1]
						: window.location.host.split('.')[0]
				}.com`;
				break;
			case 'financial':
				// TODO: Add financial product toggle
				break;
			default:
				// TODO: Add default product toggle
				break;
			}
		}
	};

	const switchProducts = [
		{
			id: 'client',
			icon: (
				<img
					alt={PRODUCT_LOGO_ALT.client}
					className="logo-img logo-client"
					src={ClientLogo}
				/>
			),
			isVisible: userProductAccess?.client,
		},
		{
			id: 'social',
			icon: (
				<img
					alt={PRODUCT_LOGO_ALT.social}
					className="logo-img logo-social"
					src={SocialLogo}
				/>
			),
			isVisible: userProductAccess?.social,
		},
		// TODO: Add this back some day...
		/* {
			id: 'financial',
			icon: <img
				src={FinancialLogo}
				alt={PRODUCT_LOGO_ALT.financial}
				className="logo-img logo-financial"
			/>,
			isVisible: userProductAccess?.financial

		}, */
		{
			id: 'sites',
			icon: (
				<img
					alt={PRODUCT_LOGO_ALT.sites}
					className="logo-img logo-sites"
					src={SitesLogo}
				/>
			),
			isVisible: userProductAccess?.sites,
		},
	];

	const handleThemeChange = (key: string) => {
		setSelectedTheme(colorsArr[key]);
		setColorCodes({
			iconColor: colorsArr[key].iconHexColor,
			iconBgColor: colorsArr[key].bgHexColor,
			linkColor: colorsArr[key].iconHexColor,
		});
		setCustomThemeEnabled(true);
	};

	// TODO: Consider lowering this to 100ms?
	const debouncedUpdate = useCallback(
		debounce((value: string, type: string, customThemeVisible: boolean) => {
			if (type && value && customThemeVisible) {
				setColorCodes((prev) => ({ ...prev, [type]: value }));
			}
		}, 300),
		[],
	);

	const handleSwitchChange = (switchName: string) => {
		setNavigationSettings((prev) => ({
			...prev,
			[switchName]: !prev[switchName as keyof NavigationSettings],
		}));
	};

	const handleDrawerFinish = () => {
		const widgetKeys = [
			'widget-header',
			'widget-header-action',
			'widget-marketing',
			'widget-banner',
			'widget-crm',
			'widget-listings',
			'widget-websites',
			'widget-financial',
			'widget-otherLinks',
			'widget-adsAndLeads',
			'widget-listingsSmc',
			'widget-landingPages',
		];

		const widgets = widgetKeys.reduce((acc: any, key) => {
			if (['widget-header-action'].includes(key)) {
				acc[key] = {};
			} else if (key === 'widget-banner') {
				acc[key] = {
					...editModalInputFields?.[key],
					...widgetCustomizationData?.widgets?.[key],
				};
			} else if (key === 'widget-header') {
				const headerImage =
					widgetCustomizationData?.widgets?.[key]?.headerImage ??
					launchpadSettingsData?.widgets?.[key]?.headerImage ??
					launchpadCustomizationDefaultState[key]?.headerImage;

				const headerTitle =
					widgetCustomizationData?.widgets?.[key]?.headerTitle ??
					launchpadSettingsData?.widgets?.[key]?.headerTitle ??
					launchpadCustomizationDefaultState[key]?.headerTitle;

				const headerTitleDescription =
					widgetCustomizationData?.widgets?.[key]?.headerTitleDescription ??
					launchpadSettingsData?.widgets?.[key]?.headerTitleDescription ??
					launchpadCustomizationDefaultState[key]?.headerTitleDescription;

				acc[key] = {
					headerImage,
					headerTitle,
					headerTitleDescription,
				};
			} else if (key === 'widget-otherLinks') {
				// Get the current complete widget data (including sections, etc.)
				const currentWidgetData =
					launchpadSettingsData?.widgets?.[key] ||
					launchpadCustomizationDefaultState[key] ||
					{};

				acc[key] = {
					// Start with the complete default/current data to preserve sections, etc.
					...currentWidgetData,
					// Then apply any custom edit modal fields
					...editModalInputFields?.[key],
					// Finally apply theme colors
					linkColor: colorCodes.linkColor,
				};
			} else if (key === 'widget-marketing') {
				// Get the current complete widget data (including sections, etc.)
				const currentWidgetData =
					launchpadSettingsData?.widgets?.[key] ||
					launchpadCustomizationDefaultState[key] ||
					{};

				acc[key] = {
					// Start with the complete default/current data to preserve sections, etc.
					...currentWidgetData,
					// Then apply any custom edit modal fields
					...editModalInputFields?.[key],
					// Finally apply theme colors (either global theme or widget-specific)
					...(editModalInputFields?.[key]?.allowWidgetCustomization
						? {
							iconColor: colorCodes.iconColor,
							iconBgColor: colorCodes.iconBgColor,
							linkColor: colorCodes.linkColor,
						}
						: {
							iconColor:
									widgetCustomizationData?.widgets?.[key]?.iconColor ||
									launchpadSettingsData?.widgets?.[key]?.iconColor ||
									currentWidgetData.iconColor,
							iconBgColor:
									widgetCustomizationData?.widgets?.[key]?.iconBgColor ||
									launchpadSettingsData?.widgets?.[key]?.iconBgColor ||
									currentWidgetData.iconBgColor,
							linkColor:
									widgetCustomizationData?.widgets?.[key]?.linkColor ||
									launchpadSettingsData?.widgets?.[key]?.linkColor ||
									currentWidgetData.linkColor,
						}),
				};
			} else if (
				[
					'widget-crm',
					'widget-listings',
					'widget-websites',
					'widget-financial',
					'widget-adsAndLeads',
					'widget-landingPages',
					'widget-listingsSmc',
				].includes(key)
			) {
				// Get the current complete widget data (including links, headerTitle, etc.)
				const currentWidgetData =
					launchpadSettingsData?.widgets?.[key] ||
					launchpadCustomizationDefaultState[key] ||
					{};

				acc[key] = {
					// Start with the complete default/current data to preserve links, headerTitle, headerIcon, etc.
					...currentWidgetData,
					// Then apply any custom edit modal fields
					...editModalInputFields?.[key],
					// Finally apply theme colors (either global theme or widget-specific)
					...(editModalInputFields?.[key]?.allowWidgetCustomization
						? {
							iconColor: colorCodes.iconColor,
							iconBgColor: colorCodes.iconBgColor,
							linkColor: colorCodes.linkColor,
						}
						: {
							iconColor:
									widgetCustomizationData?.widgets?.[key]?.iconColor ||
									launchpadSettingsData?.widgets?.[key]?.iconColor ||
									currentWidgetData.iconColor,
							iconBgColor:
									widgetCustomizationData?.widgets?.[key]?.iconBgColor ||
									launchpadSettingsData?.widgets?.[key]?.iconBgColor ||
									currentWidgetData.iconBgColor,
							linkColor:
									widgetCustomizationData?.widgets?.[key]?.linkColor ||
									launchpadSettingsData?.widgets?.[key]?.linkColor ||
									currentWidgetData.linkColor,
						}),
				};
			} else {
				acc[key] = {
					iconColor: colorCodes.iconColor,
					iconBgColor: colorCodes.iconBgColor,
					linkColor: colorCodes.linkColor,
				};
			}
			return acc;
		}, {});

		const finalSettings = {
			widgets,
			launchpadSettings: {
				navigationSettings,
				customThemeEnabled,
			},
			drawerThemeSettings: {
				iconColor: colorCodes.iconColor,
				iconBgColor: colorCodes.iconBgColor,
				linkColor: colorCodes.linkColor,
				theme: selectedTheme,
				customThemeEnabled,
			},
		};

		setWidgetCustomizationData(finalSettings);
		setIsInPreviewMode(true);
		setIsSettingsDrawerOpen(false);

		const finishedWidgets = [
			'widget-marketing',
			'widget-crm',
			'widget-otherLinks',
			'widget-listings',
			'widget-websites',
			'widget-financial',
			'widget-adsAndLeads',
			'widget-landingPages',
			'widget-listingsSmc',
		];

		const currentWidgetStatus = widgetFinishStatus || {};

		const updatedWidgetCompletionStatus = widgetKeys.reduce((status: any, key) => {
			if (finishedWidgets.includes(key)) {
				status[key] = true;
			} else {
				status[key] = currentWidgetStatus[key] ?? false;
			}
			return status;
		}, {});

		setWidgetFinishStatus(updatedWidgetCompletionStatus);

		setSnackbar({
			show: true,
			description: t('launchPadSettingsApplied'),
		});

		// Save the settings to the backend
		// handleSaveLaunchpadConfig();
	};

	return (
		<>
			<div className="bay-launchpad-content d-flex flex-column h-100 justify-content-between">
				<BayToast
					delay={3000}
					dismissible={snackbar?.dismissible ?? true}
					position={(snackbar?.position ?? 'top-end') as any}
					show={snackbar?.show}
					text={snackbar?.description}
					variant={snackbar?.variant ?? 'success'}
					onClose={() => setSnackbar({ ...snackbar, show: false })}
				/>
				<div className="my-4">
					<LaunchpadGridLayout
						currentBreakpoint={currentBreakpoint}
						currentLayout={existingLayout}
						editMode={editMode}
						getWidgetById={getWidgetByIdFn}
						gridColumns={{ lg: 5, md: 2, sm: 1 }}
						handleClose={handleClose}
						handleEditFunction={handleEditFunction}
						handleSaveLaunchpadConfig={handleSaveLaunchpadConfig}
						iseditable={iseditable}
						OnBreakpointChange={OnBreakpointChange}
						prevWidgetsRef={prevWidgetsRef}
						scroll={scroll}
						setIsRestoreDefault={setIsRestoreDefault}
						setScroll={setScroll}
						setShowConformationModal={setShowConformationModal}
						setShowModal={setIsSettingsDrawerOpen}
						setShowNoFeatureModal={setShowNoFeatureModal}
						setWidgets={setWidgets}
						snackbarRef={snackbarRef}
						widgets={widgets}
						onWidthChange={onWidthChange}
					/>

					{/* {showLaunchpadOnLogin && (
						<div className="position-fixed translate-middle-x z-1050 bg-white shadow-lg rounded-2 launchpad-login-popup">
							<div className="d-flex justify-content-between align-items-center pt-4 pe-4 pb-4 ps-5 border-bottom">
								<h5 className="m-0 fs-6 fw-semibold">{t('showLaunchpadUponLogin')}</h5>
								<button
									type="button"
									className="btn-close"
									onClick={() => setShowLaunchpadOnLogin(false)}
									aria-label="Close"
								></button>
							</div>
							<div className="p-3">
								<p>
									{t('showLaunchpadUponLoginDesc')}
								</p>

								<div className="mb-3">
									<label className="d-flex align-items-center">
										<input
											type="checkbox"
											className="me-2"
											checked={navigationSettings?.showLaunchpadOnLogin || false}
											onChange={() => {
												setNavigationSettings(prev => ({
													...prev,
													showLaunchpadOnLogin: !prev.showLaunchpadOnLogin
												}));

												// Save the setting immediately to kvstore
												const updatedSettings = {
													...launchpadSettingsData,
													launchpadSettings: {
														...(launchpadSettingsData?.launchpadSettings || {}),
														navigationSettings: {
															...(launchpadSettingsData?.launchpadSettings?.navigationSettings || {}),
															showLaunchpadOnLogin: !(navigationSettings?.showLaunchpadOnLogin || false)
														}
													}
												};

												let kvstoreId;
												if (user?.parent_account_id && user?.parent_account_id !== '') {
													kvstoreId = user?.parent_account_id;
												} else if (user?.BAY_PARENT_ACCOUNT_ID && user?.BAY_PARENT_ACCOUNT_ID !== '') {
													kvstoreId = user?.BAY_PARENT_ACCOUNT_ID;
												} else {
													kvstoreId = user?.platform === 'social' ? user?.PUBLICID : user?.ActingAsPUBLICID;
												}

												// Save to kvstore
												dispatch(updateLaunchpadApi({
													component: 'launchpadSettings',
													realm: 'BAY',
													userId: kvstoreId,
													isCompanyContext: true,
													data: updatedSettings
												}));
											}}
										/>
										{t('showLaunchpadUponLoginDesc2')}
									</label>
								</div>
							</div>
						</div>
					)} */}

					<BayToast
						autoHide={false}
						dismissible={false}
						position="bottom-center"
						show={showWarningToast}
						text="You have unsaved changes."
						variant="warning"
					/>
				</div>

				<div
					className={`d-flex flex-column launchpad-content-footer ${isProductsVisible ? 'show' : 'gap-3'}`}
				>
					{!(
						userProductAccess?.social === true &&
						userProductAccess?.client === false &&
						userProductAccess?.financial === false &&
						userProductAccess?.sites === false
					) && (
						<>
							<div
								className="d-flex align-items-center justify-content-center switch-products cursor-pointer"
								onClick={toggleProductsVisibility}
							>
								<span className="fs-6 fw-medium text-primary">
									{t('switchProducts')}
								</span>
								<BsChevronUp
									className={`text-primary ms-2 switch-products-icon ${!isProductsVisible ? 'rotated' : ''}`}
									size={16}
								/>
							</div>

							<div className="products-container">
								<div
									className={`row g-3 w-100 ${isProductsVisible ? '' : 'd-none'}`}
								>
									{switchProducts.map((product) => {
										return (
											product.isVisible && (
												<div key={product.id} className="col-auto">
													<button
														className={`product-button d-flex align-items-center justify-content-center border ${activeProduct == product.id ? 'border-primary' : 'border-gray-700'} ${product.id == 'social' && userProductAccess?.socialBayAccessProhibited ? 'opacity-50' : 'border-gray-700'} rounded-1 bg-light`}
														disabled={
															product.id == 'social' &&
															userProductAccess?.socialBayAccessProhibited
														}
														onClick={() => handleProductClick(product.id)}
													>
														<span
															className={`${product.id == 'social' && userProductAccess?.socialBayAccessProhibited ? 'opacity-50' : ''}`}
														>
															{product.icon}
														</span>
													</button>
												</div>
											)
										);
									})}
								</div>
							</div>
						</>
					)}
				</div>
			</div>

			<BayModal
				closeButton
				buttonText={isRestoreDefault ? t('restore') : t('yesCancelEdit')}
				cancelText={isRestoreDefault ? t('cancel') : t('noGoBackEdit')}
				id="layout-Customization-Modal"
				show={showConformationModal}
				submitFunc={() => handleSubmit()}
				title={
					isRestoreDefault
						? t('restoreDefaultLayout')
						: t('cancelLaunchPadCustomization')
				}
				variant={isRestoreDefault ? '' : 'danger'}
				onHideFunc={() => setShowConformationModal(false)}
			>
				{isRestoreDefault ? (
					<>
						<p>{t('launchPadRestoreDefaultModalDesc1')}</p>
						<p>{t('launchPadRestoreDefaultModalDesc2')}</p>
					</>
				) : (
					<p>{t('launchPadCancelCustomizationModalDesc')}</p>
				)}
			</BayModal>

			<BayModal
				backdrop
				closeButton
				footer={
					<Button
						className="w-100 d-flex align-items-center justify-content-center py-2 px-3 rounded"
						variant="primary"
						onClick={() => setShowNoFeatureModal(false)}
					>
						{t('ok')}
					</Button>
				}
				fullscreen
				show={showNoFeatureModal}
				title={t('featureNotAvailable')}
				onHideFunc={() => setShowNoFeatureModal(false)}
			>
				<div className="d-flex flex-column gap-4">
					<div>{t('featureNotAvailableMobileDesc')}</div>
					<div className="d-flex justify-content-center align-items-center">
						<img
							alt="featureNotAvailable"
							className="feature-not-available-img"
							crossOrigin="anonymous"
							loading="lazy"
							src="https://bay.cdn.backatyou.com/ui/placeholders/educational_widget.png"
						/>
					</div>
				</div>
			</BayModal>

			<SettingsDrawer
				bannerType={bannerType}
				colorCodes={colorCodes}
				customThemeEnabled={customThemeEnabled}
				customToggle={customToggle}
				debouncedUpdate={debouncedUpdate}
				handleDrawerFinish={handleDrawerFinish}
				handleSwitchChange={handleSwitchChange}
				handleThemeChange={handleThemeChange}
				isSettingsDrawerOpen={isSettingsDrawerOpen}
				navigationSettings={navigationSettings}
				selectedTheme={selectedTheme}
				setCustomThemeEnabled={setCustomThemeEnabled}
				setIsSettingsDrawerOpen={setIsSettingsDrawerOpen}
				t={t}
			/>
		</>
	);
};

export default LaunchpadContent;
